import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import { brands, qualities, patterns, colors } from '../mockData';
import axios from 'axios';
import { FairViewsBadge } from './FairViewsWidgets';

const Sidebar = ({ 
  selectedBrands, 
  setSelectedBrands,
  selectedColors,
  setSelectedColors,
  selectedPatterns,
  setSelectedPatterns,
  selectedCollection,
  setSelectedCollection,
  collections: collectionsFromProps
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    brands: true,
    qualities: false,
    patterns: false,
    colors: false,
    collections: false
  });
  const [localCollections, setLocalCollections] = useState([]);
  const [hoveredHelp, setHoveredHelp] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Utiliser les collections passées en props si disponibles, sinon charger depuis l'API
  const collections = collectionsFromProps?.length > 0 ? collectionsFromProps : localCollections;

  // Charger les collections depuis l'API seulement si pas passées en props
  useEffect(() => {
    if (collectionsFromProps?.length > 0) return;
    
    const fetchCollections = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/collections/`);
        setLocalCollections(response.data);
      } catch (error) {
        console.error('Erreur lors du chargement des collections:', error);
        setLocalCollections([]);
      }
    };
    
    fetchCollections();
  }, [collectionsFromProps]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const toggleBrand = (brandName) => {
    setSelectedBrands(prev => 
      prev.includes(brandName) 
        ? prev.filter(b => b !== brandName)
        : [...prev, brandName]
    );
  };

  const toggleColor = (colorName) => {
    setSelectedColors(prev => 
      prev.includes(colorName) 
        ? prev.filter(c => c !== colorName)
        : [...prev, colorName]
    );
  };

  const togglePattern = (patternName) => {
    setSelectedPatterns(prev => 
      prev.includes(patternName) 
        ? prev.filter(p => p !== patternName)
        : [...prev, patternName]
    );
  };

  return (
    <aside className="w-80 flex-shrink-0">
      {/* Category Section - Collections dynamiques */}
      <div className="bg-gray-100 p-4 rounded mb-4">
        <h3 className="font-bold text-gray-900 mb-3">NOS COLLECTIONS</h3>
        <ul className="space-y-2 text-sm">
          {collections.length > 0 ? (
            collections.map((collection, index) => (
              <li 
                key={index} 
                className="flex items-center justify-between group"
              >
                <span
                  onClick={() => setSelectedCollection && setSelectedCollection(collection.name)}
                  className={`cursor-pointer transition-colors flex-1 ${
                    selectedCollection === collection.name 
                      ? 'text-blue-600 font-semibold' 
                      : 'text-gray-700 hover:text-gray-900'
                  }`}
                >
                  {collection.name}
                </span>
                
                {/* Help Icon with Tooltip */}
                {collection.help_text && (
                  <div 
                    className="relative"
                    onMouseEnter={() => setHoveredHelp(index)}
                    onMouseLeave={() => setHoveredHelp(null)}
                  >
                    <button
                      className="text-celadon hover:text-celadon-light transition-colors ml-2"
                      aria-label="Aide"
                    >
                      <HelpCircle size={16} />
                    </button>
                    
                    {/* Tooltip */}
                    {hoveredHelp === index && (
                      <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 z-50 w-64 bg-white border-2 border-celadon rounded-lg shadow-lg p-3">
                        <div className="text-xs text-gray-700 leading-relaxed">
                          {collection.help_text}
                        </div>
                        {/* Arrow */}
                        <div className="absolute right-full top-1/2 -translate-y-1/2 w-0 h-0 border-t-8 border-t-transparent border-b-8 border-b-transparent border-r-8 border-r-celadon"></div>
                      </div>
                    )}
                  </div>
                )}
              </li>
            ))
          ) : (
            <li className="text-gray-500 text-xs">Chargement des collections...</li>
          )}
        </ul>
      </div>

      {/* Filter Button */}
      <button 
        onClick={() => setShowFilters(!showFilters)}
        className="w-full bg-gray-100 p-4 rounded mb-4 text-left font-bold flex items-center justify-between hover:bg-gray-200 transition-colors"
      >
        FILTRER
        {showFilters ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </button>

      {/* Filters */}
      {showFilters && (
        <div className="space-y-4">
          {/* Brand Filter */}
          <div className="border-b pb-4">
            <button
              onClick={() => toggleSection('brands')}
              className="w-full flex items-center justify-between text-left font-medium mb-3"
            >
              Marque de la toile
              {expandedSections.brands ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
            {expandedSections.brands && (
              <div className="space-y-2">
                {brands.map((brand, index) => (
                  <label key={index} className="flex items-center gap-2 cursor-pointer text-sm hover:text-gray-900">
                    <input
                      type="checkbox"
                      checked={selectedBrands.includes(brand.name)}
                      onChange={() => toggleBrand(brand.name)}
                      className="w-4 h-4 text-celadon border-gray-300 rounded focus:ring-[#2ECC71]"
                    />
                    <span>{brand.name} ({brand.count})</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Quality Filter */}
          <div className="border-b pb-4">
            <button
              onClick={() => toggleSection('qualities')}
              className="w-full flex items-center justify-between text-left font-medium mb-3"
            >
              Qualité de la toile
              {expandedSections.qualities ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
            {expandedSections.qualities && (
              <div className="space-y-2">
                {qualities.map((quality, index) => (
                  <label key={index} className="flex items-center gap-2 cursor-pointer text-sm hover:text-gray-900">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-celadon border-gray-300 rounded focus:ring-[#2ECC71]"
                    />
                    <span>{quality.name} ({quality.count})</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Pattern Filter */}
          <div className="border-b pb-4">
            <button
              onClick={() => toggleSection('patterns')}
              className="w-full flex items-center justify-between text-left font-medium mb-3"
            >
              Motifs
              {expandedSections.patterns ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
            {expandedSections.patterns && (
              <div className="space-y-2">
                {patterns.map((pattern, index) => (
                  <label key={index} className="flex items-center gap-2 cursor-pointer text-sm hover:text-gray-900">
                    <input
                      type="checkbox"
                      checked={selectedPatterns.includes(pattern.name)}
                      onChange={() => togglePattern(pattern.name)}
                      className="w-4 h-4 text-celadon border-gray-300 rounded focus:ring-[#2ECC71]"
                    />
                    <span>{pattern.name} ({pattern.count})</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Color Filter */}
          <div className="border-b pb-4">
            <button
              onClick={() => toggleSection('colors')}
              className="w-full flex items-center justify-between text-left font-medium mb-3"
            >
              Couleur de la toile
              {expandedSections.colors ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
            {expandedSections.colors && (
              <div className="grid grid-cols-4 gap-2">
                {colors.map((color, index) => (
                  <button
                    key={index}
                    onClick={() => toggleColor(color.name)}
                    className={`relative group`}
                    title={`${color.name} (${color.count})`}
                  >
                    <div 
                      className={`w-full aspect-square rounded border-2 ${
                        selectedColors.includes(color.name) 
                          ? 'border-celadon ring-2 ring-[#2ECC71]' 
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                      style={{ backgroundColor: color.hex }}
                    >
                      {color.hex === '#FFFFFF' && (
                        <div className="absolute inset-0 border border-gray-200 rounded" />
                      )}
                    </div>
                    <span className="text-xs mt-1 block truncate">{color.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* FairViews Widget Badge */}
      <div className="mt-6 flex justify-center">
        <FairViewsBadge />
      </div>
    </aside>
  );
};

export default Sidebar;